import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import ProductCard from "../components/ProductCard";
import { fetchProducts, fetchCategories } from "../lib/api";

const Home = () => {
  const [featured, setFeatured] = useState([]);
  const [photography, setPhotography] = useState([]);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    fetchProducts({ featured: true }).then(setFeatured).catch(() => {});
    fetchProducts({ category: "photography" }).then(setPhotography).catch(() => {});
    fetchCategories().then(setCategories).catch(() => {});
  }, []);

  return (
    <main className="max-w-[1400px] mx-auto px-6">
      {/* Hero */}
      <section className="mt-8 fade-up">
        <div className="relative overflow-hidden rounded-3xl bg-[#0e2a1e] px-8 md:px-16 py-20 md:py-28">
          <div className="absolute inset-0 opacity-[0.06] bg-[radial-gradient(circle_at_80%_20%,#a4d61e_0%,transparent_50%)]" />
          <div className="relative max-w-2xl">
            <h1 className="font-serif-brand text-4xl md:text-6xl font-semibold text-[#f4f2ea] leading-[1.1]">
              High Quality. Low Prices. Built for the Community.
            </h1>
            <p className="text-[#f4f2ea]/80 text-lg mt-6 max-w-md leading-relaxed">
              Your new go-to spot in Mississauga for budget-friendly apparel, footwear, and home goods.
            </p>
            <Link
              to="/shop"
              className="inline-block mt-8 bg-[#a4d61e] text-[#0e2a1e] font-semibold text-sm px-8 py-3.5 rounded-full hover:bg-[#b6e63a] hover:shadow-lg hover:shadow-[#a4d61e]/20 transition-all"
            >
              Shop New Arrivals
            </Link>
          </div>
        </div>
      </section>

      {/* Shop by category */}
      <section className="mt-20">
        <div className="flex items-end justify-between mb-8">
          <div>
            <h2 className="font-serif-brand text-3xl md:text-4xl font-semibold text-[#0e2a1e]">
              Shop by category
            </h2>
            <p className="text-[#0e2a1e]/60 text-sm mt-2">
              Tap to browse products in that department.
            </p>
          </div>
          <Link to="/shop" className="text-sm text-[#0e2a1e] font-medium hover:text-[#a4820e] transition-colors whitespace-nowrap">
            View all →
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {categories.map((c) => (
            <Link
              key={c.id}
              to={`/shop/${c.id}`}
              className="card-zoom group relative overflow-hidden rounded-2xl aspect-[4/3]"
            >
              <img src={c.image} alt={c.name} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
              <span className="absolute bottom-5 left-5 font-serif-brand text-2xl font-medium text-white">
                {c.name}
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* Photography Services */}
      {photography.length > 0 && (
        <section className="mt-24">
          <div className="relative overflow-hidden rounded-3xl bg-[#0e2a1e] px-8 md:px-14 py-12 md:py-16">
            <div className="absolute inset-0 opacity-[0.06] bg-[radial-gradient(circle_at_15%_85%,#a4d61e_0%,transparent_45%)]" />
            <div className="relative flex flex-col md:flex-row md:items-end justify-between gap-4">
              <div className="max-w-xl">
                <span className="inline-block bg-[#a4d61e] text-[#0e2a1e] text-xs font-semibold px-3 py-1 rounded-full mb-4">
                  New
                </span>
                <h2 className="font-serif-brand text-3xl md:text-4xl font-semibold text-[#f4f2ea]">
                  Photography Services
                </h2>
                <p className="text-[#f4f2ea]/75 mt-3 leading-relaxed">
                  Budget-friendly, high-quality photo sessions right here in Mississauga — family minis, professional headshots, small-business product shoots, and event coverage. Book online just like any other product.
                </p>
              </div>
              <Link
                to="/shop/photography"
                className="shrink-0 bg-[#a4d61e] text-[#0e2a1e] font-semibold text-sm px-7 py-3 rounded-full hover:bg-[#b6e63a] transition-colors whitespace-nowrap"
              >
                View all sessions →
              </Link>
            </div>

            <div className="relative grid grid-cols-2 lg:grid-cols-4 gap-5 mt-10">
              {photography.map((p) => (
                <Link
                  key={p.id}
                  to={`/product/${p.id}`}
                  className="card-zoom group block bg-[#f4f2ea] rounded-xl overflow-hidden"
                >
                  <div className="aspect-[4/3] overflow-hidden">
                    <img src={p.image} alt={p.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="p-4">
                    <span className="text-[11px] font-semibold uppercase tracking-wide text-[#2f6b1e]">
                      {p.tagline}
                    </span>
                    <h3 className="text-sm font-medium text-[#0e2a1e] mt-1 group-hover:text-[#a4820e] transition-colors">
                      {p.name}
                    </h3>
                    <p className="text-sm text-[#0e2a1e]/70 mt-1">
                      from ${(p.variants && p.variants[0] ? p.variants[0].price : p.price).toFixed(2)}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Featured */}
      <section className="mt-20">
        <div className="flex items-end justify-between mb-8">
          <div>
            <h2 className="font-serif-brand text-3xl md:text-4xl font-semibold text-[#0e2a1e]">
              Featured
            </h2>
            <p className="text-[#0e2a1e]/60 text-sm mt-2">Our hand-picked favourites.</p>
          </div>
          <Link to="/shop" className="text-sm text-[#0e2a1e] font-medium hover:text-[#a4820e] transition-colors whitespace-nowrap">
            Shop all →
          </Link>
        </div>
        {featured.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {featured.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        ) : (
          <p className="text-[#0e2a1e]/60 text-sm">
            No featured products yet. Browse <Link to="/shop" className="text-[#a4820e] underline">the shop</Link>.
          </p>
        )}
      </section>
    </main>
  );
};

export default Home;
